import { SSEData, SSEEventType, GameEventUpdate } from './types'

export const parseSSEData = (data: string): SSEData => {
  const lines = data.split('\n').filter(Boolean)

  if (lines.length === 1 && lines[0].startsWith('data: No live game')) {
    return {
      type: 'connection',
      data: 'No live game',
    }
  }

  const eventType =
    (lines.find((line) => line.startsWith('event: '))?.slice(7) as SSEEventType) ??
    'connection'

  const eventData = lines.find((line) => line.startsWith('data: '))?.slice(6) ?? ''

  try {
    return {
      type: eventType,
      data:
        eventType === 'eventUpdate'
          ? (JSON.parse(eventData) as GameEventUpdate)
          : eventData,
    }
  } catch (err) {
    if (eventData.includes('No live game') || eventData.includes('Game ended')) {
      return {
        type: 'connection',
        data: eventData,
      }
    }
    throw err
  }
}

interface StreamHandlers {
  onData: (data: SSEData) => void
  onError: (error: Error) => void
  onClose: () => void
}

export const readStream = async (
  reader: ReadableStreamDefaultReader<Uint8Array>,
  { onData, onError, onClose }: StreamHandlers
): Promise<void> => {
  const decoder = new TextDecoder()
  let buffer = ''

  try {

    const firstChunk = await reader.read()
    if (firstChunk.value) {
      const initialContent = decoder.decode(firstChunk.value, { stream: true })

      if (
        initialContent.trim().startsWith('<!DOCTYPE html>') ||
        initialContent.trim().startsWith('<html')
      ) {
        console.error('Received HTML content instead of SSE stream:', initialContent)
        throw new Error(
          'Received HTML response instead of SSE stream. This may indicate an authentication error or invalid endpoint.'
        )
      }
      buffer = initialContent

      if (buffer.includes('data: No live game')) {
        const parsedData = parseSSEData(buffer)
        onData(parsedData)
        onClose()
        return
      }
    }

    while (true) {
      const { value, done } = await reader.read()
      if (done) {
        console.log('SSE stream complete')
        onClose()
        break
      }

      const chunk = decoder.decode(value, { stream: true })

      if (
        chunk.trim().startsWith('<!DOCTYPE html>') ||
        chunk.trim().startsWith('<html')
      ) {
        console.error('Received HTML content in SSE stream:', chunk)
        throw new Error('Received HTML content in SSE stream')
      }

      buffer += chunk

      const [messages, remainingBuffer] = buffer
        .split('\n\n')
        .reduce<[string[], string]>(
          ([complete, last], curr, i, arr) =>
            i === arr.length - 1 ? [complete, curr] : [[...complete, curr], last],
          [[], '']
        )

      buffer = remainingBuffer

      for (const message of messages.filter(Boolean)) {
        try {
          if (!message.includes('data:') && !message.includes('event:')) {
            console.warn('Received non-SSE formatted message:', message)
            continue
          }
          const parsedData = parseSSEData(message)
          onData(parsedData)

          if (
            parsedData.type === 'connection' &&
            (parsedData.data === 'No live game' || parsedData.data === 'Game ended')
          ) {
            console.log('Received game end or no game message, closing stream')
            onClose()
            return
          }
        } catch (err) {
          console.error('Error parsing SSE message:', message, err)
          if (err instanceof Error) {
            onError(err)
          }
        }
      }
    }
  } catch (err) {
    // Don't treat AbortError as an error when it's from manual disconnection
    if (err instanceof Error && err.name === 'AbortError') {
      console.log('SSE stream aborted (manual disconnect)')
      onClose()
      return
    }

    console.error('SSE stream error:', err)
    if (err instanceof Error) {
      onError(err)
    } else {
      onError(new Error('Unknown error occurred while reading SSE stream'))
    }
  }
}
